import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, useMap, Circle, CircleMarker, Marker, Polyline, Polygon, Tooltip } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.gridlayer.googlemutant";
import { useRoom } from "./RoomProvider";
import { driverPuckColor } from "../../hooks/useLiveDrivers";
import { useOsrmRoute } from "../../hooks/useOsrmRoute";
import { apiClient } from "../../lib/api";

/**
 * RoomCanvas — real geography, layered.
 *
 *   Tiles       CartoDB Dark Matter (free, no key)
 *   World 1     Physical roads + hospital glows (below)
 *   World 2     Coverage rings · driver pucks · trip halos
 *   World 3     Trust tethers (only when a trip is focused)
 *   World 4     Prediction ghosts (only when Nova is watching)
 *   Ambient     Slow orbital scan light (always) so the room feels awake
 *
 * The camera talks to the map via setView on `preset` changes.
 * Same substrate. The room re-organizes itself around the focus.
 */

// City → real lat/lng zoom for camera presets. Nationwide expansion —
// this is not an exhaustive market list, just quick-jump shortcuts.
// Chicago + Houston are real markets; the default view is "world" so
// the room never implies one city matters more than wherever the
// live trips actually are.
export const CAMERA_LATLNG = {
  world:      { center: [36.5,  -89.0],   zoom: 4.5  },
  // Illinois
  chicago:    { center: [41.8781, -87.6298], zoom: 10.6 },
  // North Carolina
  charlotte:  { center: [35.2271, -80.8431], zoom: 11.0 },
  raleigh:    { center: [35.7796, -78.6382], zoom: 11.0 },
  durham:     { center: [35.9940, -78.8986], zoom: 11.5 },
  // South Carolina
  columbia:   { center: [34.0007, -81.0348], zoom: 11.5 },
  charleston: { center: [32.7765, -79.9311], zoom: 11.5 },
  greenville: { center: [34.8526, -82.3940], zoom: 11.5 },
  // Georgia
  atlanta:    { center: [33.7490, -84.3880], zoom: 11.0 },
  savannah:   { center: [32.0809, -81.0912], zoom: 12.0 },
  // Legacy (kept so any existing /go houston commands don't break)
  houston:    { center: [29.7604, -95.3698], zoom: 10.4 },
};

// ─── camera controller ────────────────────────────────────────────
function CameraController() {
  const map = useMap();
  const { camera, focus, liveTrips, liveDrivers } = useRoom();

  // React to camera preset changes
  useEffect(() => {
    if (!camera) return;
    if (camera.center) {
      map.flyTo(camera.center, camera.zoom, { duration: 0.9, easeLinearity: 0.3 });
    }
  }, [camera, map]);

  // Zoom to focused entity's real coordinates — WITH MUSCLE.
  useEffect(() => {
    if (!focus) return;
    const opts = { duration: 1.4, easeLinearity: 0.15 };
    if (focus.kind === "realTrip") {
      const t = (liveTrips || []).find(x => x.trip_id === focus.id);
      if (t?.pickup?.lat != null && t?.dropoff?.lat != null) {
        const cx = (t.pickup.lat + t.dropoff.lat) / 2;
        const cy = (t.pickup.lng + t.dropoff.lng) / 2;
        map.flyTo([cx, cy], 13, opts);
      } else if (t?.pickup?.lat != null) {
        map.flyTo([t.pickup.lat, t.pickup.lng], 14, opts);
      }
    } else if (focus.kind === "liveDriver") {
      const d = (liveDrivers || []).find(x => x.driver_id === focus.id);
      if (d?.last_seen_lat != null) map.flyTo([d.last_seen_lat, d.last_seen_lng], 15, opts);
    }
  }, [focus, map, liveTrips, liveDrivers]);

  return null;
}

// ─── vehicle/facility icon paths (Lucide "car", "ambulance",
// "accessibility", "hospital") — hand-copied path data so map markers
// render as real icons without pulling react-dom/server into the bundle
// just to rasterize a handful of static SVGs.
const LUCIDE_ICON_PATHS = {
  car: [
    ["path", "M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"],
    ["circle", "7", "17", "2"],
    ["path", "M9 17h6"],
    ["circle", "17", "17", "2"],
  ],
  ambulance: [
    ["path", "M10 10H6"],
    ["path", "M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"],
    ["path", "M19 18h2a1 1 0 0 0 1-1v-3.28a1 1 0 0 0-.684-.948l-1.923-.641a1 1 0 0 1-.578-.502l-1.539-3.076A1 1 0 0 0 16.382 8H14"],
    ["path", "M8 8v4"],
    ["path", "M9 18h6"],
    ["circle", "17", "18", "2"],
    ["circle", "7", "18", "2"],
  ],
  accessibility: [
    ["circle", "16", "4", "1"],
    ["path", "m18 19 1-7-6 1"],
    ["path", "m5 8 3-3 5.5 3-2.36 3.5"],
    ["path", "M4.24 14.5a5 5 0 0 0 6.88 6"],
    ["path", "M13.76 17.5a5 5 0 0 0-6.88-6"],
  ],
  hospital: [
    ["path", "M12 6v4"],
    ["path", "M14 14h-4"],
    ["path", "M14 18h-4"],
    ["path", "M14 8h-4"],
    ["path", "M18 12h2a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2h2"],
    ["path", "M18 22V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v18"],
  ],
  person: [
    ["path", "M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"],
    ["circle", "12", "7", "4"],
  ],
};

function lucideSvg(name, size = 16, color = "#04121e") {
  const nodes = LUCIDE_ICON_PATHS[name] || LUCIDE_ICON_PATHS.car;
  const inner = nodes
    .map((n) => (n[0] === "circle"
      ? `<circle cx="${n[1]}" cy="${n[2]}" r="${n[3]}" />`
      : `<path d="${n[1]}" />`))
    .join("");
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">${inner}</svg>`;
}

// Map a driver's vehicle_type (from /tpt/map/candidates) to the right icon —
// dispatchers need to tell a wheelchair van from a sedan at a glance.
function iconForVehicleType(vehicleType) {
  const v = String(vehicleType || "").toLowerCase();
  if (v === "wheelchair") return "accessibility";
  if (v === "stretcher") return "ambulance";
  return "car"; // sedan, suv, or unknown
}

// ─── custom driver puck (icon divIcon) — floats above roads ────────
function driverIcon(color = "#4bb8f8", focused = false, vehicleType = null) {
  const size = focused ? 36 : 24;
  const core = Math.round(size * 0.66);
  const iconSize = Math.round(core * 0.56);
  const html = `
    <div class="drv-badge${focused ? " drv-badge--focused" : ""}" style="--dc:${color};width:${size}px;height:${size}px;">
      <div class="drv-badge-core" style="width:${core}px;height:${core}px;">
        ${lucideSvg(iconForVehicleType(vehicleType), iconSize, "#04121e")}
      </div>
    </div>`;
  return L.divIcon({
    html, className: "drv-puck",
    iconSize: [size, size], iconAnchor: [size / 2, size / 2],
  });
}

// hospital glow — soft, always breathing (night-shift soul), real hospital
// icon at the core instead of a bare dot. Color reflects real delay
// severity (from /api/floor/hospitals/delay) — grey when quiet (no active
// runs there, never a fabricated risk signal), green on pace, amber/red when
// active trips are running behind their scheduled pickup.
const HOSPITAL_SEVERITY_COLOR = {
  quiet: "#64748b",
  onpace: "#7fd8b7",
  delayed: "#f59e0b",
  severe: "#ef4444",
};

function hospitalIcon(severity = "onpace") {
  const color = HOSPITAL_SEVERITY_COLOR[severity] || HOSPITAL_SEVERITY_COLOR.onpace;
  const html = `
    <div class="hosp-glow" style="--hc:${color}">
      <div class="hg-pulse"></div>
      <div class="hg-badge">${lucideSvg("hospital", 16, "#04150f")}</div>
    </div>`;
  return L.divIcon({
    html, className: "hosp-icon",
    iconSize: [42, 42], iconAnchor: [21, 21],
  });
}

// Polls the real delay-severity endpoint for a fixed list of named hospitals
// every 20s — a light, non-authoritative overlay signal, not a new live feed.
function useHospitalDelays(hospitals) {
  const [delays, setDelays] = useState({});
  const names = useMemo(() => (hospitals || []).map(h => h.name).join(","), [hospitals]);
  useEffect(() => {
    if (!names) return;
    let cancel = false;
    const pull = async () => {
      try {
        const { data } = await apiClient.get(`/floor/hospitals/delay?names=${encodeURIComponent(names)}`);
        if (cancel) return;
        const map = {};
        (data?.hospitals || []).forEach(h => { map[h.name] = h; });
        setDelays(map);
      } catch { /* silent — overlay only */ }
    };
    pull();
    const t = setInterval(pull, 20000);
    return () => { cancel = true; clearInterval(t); };
  }, [names]);
  return delays;
}

// trip halo — different colors per trust band · pinned adds a marker, SOS
// enlarges. Fixes VA Walkthrough finding: rider/trip pickups were bare
// colored rings, indistinguishable from coverage circles at a glance — now
// carry a person icon in the core the same way hospitals/drivers do.
function tripHaloIcon(color, focused, pinned = false, sos = false, urgent = false) {
  const base = sos ? 34 : (focused ? 28 : 20);
  const core = Math.round(base * 0.7);
  const iconSize = Math.round(core * 0.56);
  const html = `
    <div class="trip-halo ${sos ? "sos" : ""} ${urgent ? "sla-urgent" : ""}" style="--halo:${color};--s:${base}px">
      <div class="th-outer"></div>
      <div class="th-inner"></div>
      <div class="th-core" style="background:${color};width:${core}px;height:${core}px;">
        ${lucideSvg("person", iconSize, "#04121e")}
      </div>
      ${pinned ? '<div class="th-pin">P</div>' : ""}
    </div>`;
  return L.divIcon({
    html, className: "trip-halo-wrap",
    iconSize: [base*2.4, base*2.4], iconAnchor: [base*1.2, base*1.2],
  });
}

// SLA read on an unassigned trip — real data (created_at), not decoration.
// Fresh (<5m) green, aging (5-10m) amber, urgent (10m+) red + flashing —
// so the halo's color/pulse actually means something a VA can act on.
function tripSlaInfo(trip) {
  if (!trip?.created_at) return null;
  const elapsedMin = (Date.now() - new Date(trip.created_at).getTime()) / 60000;
  if (elapsedMin >= 10) return { color: "#ef4444", label: "urgent", urgent: true, mins: elapsedMin };
  if (elapsedMin >= 5) return { color: "#f59e0b", label: "aging", urgent: false, mins: elapsedMin };
  return { color: "#34d399", label: "fresh", urgent: false, mins: elapsedMin };
}

// ─── the map itself ───────────────────────────────────────────────
export default function RoomCanvas() {
  const { network, focus, focusOn, mood, liveTrips, haloColorForStatus: colorForStatus,
          assignTrip: assignTripFromMap, liveDrivers } = useRoom();
  const focusedRealTripId = focus?.kind === "realTrip" ? focus.id : null;
  const focusedLiveDriverId = focus?.kind === "liveDriver" ? focus.id : null;

  // Google Traffic overlay — toggleable. Uses the same Google Maps key as
  // /tp/map so no new env work needed. Non-authoritative visual layer.
  const [trafficOn, setTrafficOn] = useState(false);
  const [weatherOn, setWeatherOn] = useState(false);
  const [zonesOn, setZonesOn] = useState(false);
  const [heatmapOn, setHeatmapOn] = useState(false);
  const [coverageOn, setCoverageOn] = useState(false);
  const [deadheadOn, setDeadheadOn] = useState(false);
  const [overlayRange, setOverlayRange] = useState("today");
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Set while a trip halo is being dragged — drives the visible 500m
  // drop-zone rings around eligible drivers so the drag target is legible.
  const [draggingTrip, setDraggingTrip] = useState(null);

  const focusedRealTrip = useMemo(
    () => (liveTrips || []).find(t => t.trip_id === focusedRealTripId),
    [focusedRealTripId, liveTrips],
  );

  // Only paint real trips that have geocoded pickup coords.
  const realHalos = useMemo(
    () => (liveTrips || []).filter(t => t?.pickup?.lat != null && t?.pickup?.lng != null),
    [liveTrips],
  );

  // Fixes VA Walkthrough Finding 7 — Nova's counters are real, but the map
  // only ever painted trips with a geocoded pickup, so the room looked
  // nearly empty while claiming dozens/hundreds of live trips. Say so.
  const ungeocodedCount = Math.max(0, (liveTrips || []).length - realHalos.length);

  const CARTO_KEY = process.env.REACT_APP_CARTO_API_KEY;

  return (
    <div className="room-canvas-wrap">
      <MapContainer
        center={CAMERA_LATLNG.world.center}
        zoom={CAMERA_LATLNG.world.zoom}
        zoomControl={false}
        attributionControl={false}
        className="room-map"
        preferCanvas={true}
        maxZoom={18}
        minZoom={4}
      >
        {/* Basemap — CARTO's free raster tiles now require an API key
            (basemaps.cartocdn.com started watermarking unauthenticated
            requests with "API KEY REQUIRED" in 2026). If a key is set via
            REACT_APP_CARTO_API_KEY, use CARTO's real dark tiles; otherwise
            fall back to OpenStreetMap's always-free tiles and fake the dark
            look with a CSS invert filter on just the tile pane (markers and
            overlays live in a different Leaflet pane, so they're untouched). */}
        {CARTO_KEY ? (
          <>
            <TileLayer
              url={`https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png?key=${CARTO_KEY}`}
              subdomains={["a", "b", "c", "d"]}
            />
            <TileLayer
              url={`https://{s}.basemaps.cartocdn.com/dark_only_labels/{z}/{x}/{y}{r}.png?key=${CARTO_KEY}`}
              subdomains={["a", "b", "c", "d"]}
              opacity={0.5}
            />
          </>
        ) : (
          <TileLayer
            className="room-map-osm-dark"
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            subdomains={["a", "b", "c"]}
            maxZoom={19}
          />
        )}

        {/* Google Traffic overlay — only when toggled */}
        {trafficOn && <GoogleTrafficLayer />}

        {/* Weather severity overlay — precipitation + wind */}
        {weatherOn && <WeatherLayer />}

        {/* Operating zones / heat-map / coverage / deadhead-mismatch —
            translucent zone-polygon overlays sharing one date range. */}
        {zonesOn && <ZonesOverlay />}
        {heatmapOn && <HeatmapOverlay range={overlayRange} />}
        {coverageOn && <CoverageOverlay range={overlayRange} />}
        {deadheadOn && <DeadheadOverlay range={overlayRange} />}

        <CameraController />

        {/* ─── WORLD 2 · OPERATIONAL — real trip halos (LIVE · DRAGGABLE for assign) ─── */}
        {realHalos.map(t => {
          const focused = t.trip_id === focusedRealTripId;
          const statusU = String(t.status).toUpperCase();
          const awaitingAssignment = ["REQUESTED", "PENDING"].includes(statusU);
          const sla = awaitingAssignment ? tripSlaInfo(t) : null;
          const color = sla ? sla.color : (colorForStatus ? colorForStatus(t.status) : "#7fd8b7");
          const canDrag = ["REQUESTED", "PENDING", "TP_ACCEPTED", "OFFERED", "ASSIGNED", "EXPIRED_OFFER"].includes(statusU);
          return (
            <Marker
              key={`live-${t.trip_id}`}
              position={[t.pickup.lat, t.pickup.lng]}
              icon={tripHaloIcon(color, focused, false, false, sla?.urgent)}
              draggable={canDrag}
              eventHandlers={{
                click: () => focusOn("realTrip", t.trip_id),
                dragstart: (e) => {
                  e.target._origPos = e.target.getLatLng();
                  document.body.classList.add("floor-dragging-trip");
                  setDraggingTrip(t);
                },
                dragend: async (e) => {
                  document.body.classList.remove("floor-dragging-trip");
                  setDraggingTrip(null);
                  const drop = e.target.getLatLng();
                  const origPos = e.target._origPos;
                  const dropped = await handleTripDrop(t, drop, liveDrivers, assignTripFromMap);
                  if (!dropped && origPos) {
                    // Snap back — no valid drop target
                    e.target.setLatLng(origPos);
                  }
                },
              }}
              zIndexOffset={focused ? 1000 : 500}
            >
              <Tooltip direction="top" opacity={0.9} offset={[0, -14]}>
                {t.rider?.name || "trip"} · {t.status}
                {t.assigned_tp?.tp_name ? ` · ${t.assigned_tp.tp_name}` : ""}
                {sla ? ` · waiting ${Math.round(sla.mins)}m` : ""}
                {canDrag ? " · drag onto a driver to assign" : ""}
              </Tooltip>
            </Marker>
          );
        })}

        {/* ─── Drop-zone rings — while dragging a trip halo, show exactly
             where "close enough to assign" means (500m), on every eligible
             driver puck, instead of an invisible threshold. ─── */}
        {draggingTrip && (liveDrivers || [])
          .filter(d => d.last_seen_lat != null && d.last_seen_lng != null)
          .map(d => (
            <Circle
              key={`dropzone-${d.driver_id}`}
              center={[d.last_seen_lat, d.last_seen_lng]}
              radius={_DROP_THRESHOLD_METERS}
              className="drop-zone-ring"
              pathOptions={{ color: "#7fd8b7", fillColor: "#7fd8b7", fillOpacity: 0.10, weight: 1.5, dashArray: "4 5" }}
            />
          ))}

        {/* ─── real focused trip — tether pickup → dropoff (real road route via OSRM, honest straight-line fallback) ─── */}
        {focusedRealTrip?.pickup?.lat != null && focusedRealTrip?.dropoff?.lat != null && (
          <>
            <FocusedTripRoute trip={focusedRealTrip} colorForStatus={colorForStatus} />
            <CircleMarker
              center={[focusedRealTrip.dropoff.lat, focusedRealTrip.dropoff.lng]}
              radius={8}
              pathOptions={{ color: "#7fd8b7", fillColor: "#7fd8b7", fillOpacity: 0.55, weight: 1.5 }}
            />
          </>
        )}

        {/* ─── WORLD 2 · OPERATIONAL — coverage · drivers · halos ─── */}
        {/* Driver pucks · REAL GPS — from /tpt/map/candidates. Clickable —
            opens the real driver's detail card in the workspace. */}
        {liveDrivers.map(d => {
          if (d.last_seen_lat == null || d.last_seen_lng == null) return null;
          const color = driverPuckColor(d);
          const focused = d.driver_id === focusedLiveDriverId;
          return (
            <Marker
              key={`live-drv-${d.driver_id}`}
              position={[d.last_seen_lat, d.last_seen_lng]}
              icon={driverIcon(color, focused, d.vehicle_type)}
              zIndexOffset={focused ? 1900 : 1800}
              eventHandlers={{ click: () => focusOn("liveDriver", d.driver_id) }}
            >
              <Tooltip direction="top" opacity={0.9} offset={[0, -6]}>
                {d.name} · {d.shift_status || "on shift"}{d.current_trip_id ? " · on trip" : ""}
              </Tooltip>
            </Marker>
          );
        })}


        {/* ─── WORLD 3 · TRUST — tether pickup → dropoff on focused trip ─── */}
      </MapContainer>

      {/* ─── AMBIENT · orbital scan light (always on) ─── */}
      <div className="scan-ring">
        <div className="scan-wedge" />
      </div>

      {/* ─── Utility drawer — one slide-out for traffic / weather / legend
             instead of 3 permanently floating buttons in the way. ─── */}
      <button
        className={`utility-drawer-toggle ${drawerOpen ? "utility-drawer-toggle--on" : ""}`}
        onClick={() => setDrawerOpen((v) => !v)}
        data-testid="utility-drawer-toggle"
        title="map layers & legend"
      >
        ☰
      </button>
      <div className={`utility-drawer ${drawerOpen ? "utility-drawer--open" : ""}`} data-testid="utility-drawer">
        <div className="ud-header">
          <span>map layers</span>
          <button onClick={() => setDrawerOpen(false)} data-testid="utility-drawer-close">×</button>
        </div>
        <button
          className={`mc-btn ${trafficOn ? "mc-btn--on" : ""}`}
          onClick={() => setTrafficOn((v) => !v)}
          data-testid="traffic-toggle"
        >
          <span className="mc-dot" />
          <span>traffic {trafficOn ? "· live" : ""}</span>
        </button>
        <button
          className={`mc-btn ${weatherOn ? "mc-btn--on" : ""}`}
          onClick={() => setWeatherOn((v) => !v)}
          data-testid="weather-toggle"
          title={process.env.REACT_APP_OPENWEATHER_API_KEY
            ? "Toggle live precipitation overlay"
            : "Set REACT_APP_OPENWEATHER_API_KEY to enable live precipitation"}
        >
          <span className="mc-dot" />
          <span>
            weather
            {weatherOn && process.env.REACT_APP_OPENWEATHER_API_KEY && " · live"}
            {weatherOn && !process.env.REACT_APP_OPENWEATHER_API_KEY && " · needs key"}
          </span>
        </button>
        <div className="ud-divider" />
        <div className="ml-title">operating zones</div>
        <button className={`mc-btn ${zonesOn ? "mc-btn--on" : ""}`} onClick={() => setZonesOn((v) => !v)} data-testid="zones-toggle">
          <span className="mc-dot" /><span>zones</span>
        </button>
        <button className={`mc-btn ${heatmapOn ? "mc-btn--on" : ""}`} onClick={() => setHeatmapOn((v) => !v)} data-testid="heatmap-toggle">
          <span className="mc-dot" /><span>heat map</span>
        </button>
        <button className={`mc-btn ${coverageOn ? "mc-btn--on" : ""}`} onClick={() => setCoverageOn((v) => !v)} data-testid="coverage-toggle">
          <span className="mc-dot" /><span>coverage</span>
        </button>
        <button className={`mc-btn ${deadheadOn ? "mc-btn--on" : ""}`} onClick={() => setDeadheadOn((v) => !v)} data-testid="deadhead-toggle">
          <span className="mc-dot" /><span>deadhead mismatch</span>
        </button>
        {(heatmapOn || coverageOn || deadheadOn) && (
          <select
            className="ud-range-select"
            value={overlayRange}
            onChange={(e) => setOverlayRange(e.target.value)}
            data-testid="overlay-range-select"
          >
            <option value="today">today</option>
            <option value="7d">last 7 days</option>
            <option value="30d">last 30 days</option>
          </select>
        )}
        <OverlayLegend heatmapOn={heatmapOn} coverageOn={coverageOn} deadheadOn={deadheadOn} />
        <div className="ud-divider" />
        <div className="ml-title">what's on the map</div>
        <LegendRow icon="person" color="#4bb8f8" label="trip pickup"
                   detail="green fresh · amber aging 5m+ · red urgent 10m+ waiting for a driver" />
        <LegendRow icon="car" color="#4bb8f8" label="driver · sedan"
                   detail="color = shift state · blue ready, green on a trip, grey offline" />
        <LegendRow icon="accessibility" color="#4bb8f8" label="driver · wheelchair van" detail="same shift-state coloring" />
        <LegendRow icon="ambulance" color="#4bb8f8" label="driver · stretcher" detail="same shift-state coloring" />
        <LegendRow icon="hospital" color="#7fd8b7" label="hospital / facility"
                   detail="color = delay severity · grey quiet, green on pace, amber/red running behind" />
        <div className="ml-note">faint circles = a provider's coverage radius, not a live entity · dashed ring while dragging = the 500m "close enough to assign" zone · click any marker for detail</div>
      </div>

      {/* ─── Honest density badge — Nova's counters vs. what's actually plotted ─── */}
      {ungeocodedCount > 0 && (
        <div className="map-density-badge" data-testid="floor-map-density-badge"
             title="These live trips don't have a geocoded pickup yet, so they can't be plotted — the count isn't hidden, just not on the map.">
          +{ungeocodedCount} live · no mapped location
        </div>
      )}

      {/* ─── AMBIENT · Nova's attention halo drifts toward focus target ─── */}
      <NovaHalo />
    </div>
  );
}
function LegendRow({ icon, color, label, detail }) {
  return (
    <div className="ml-row">
      <span className="ml-dot" style={{ background: color }}
            dangerouslySetInnerHTML={{ __html: lucideSvg(icon, 12, "#04121e") }} />
      <div>
        <div className="ml-label">{label}</div>
        <div className="ml-detail">{detail}</div>
      </div>
    </div>
  );
}

/**
 * FocusedTripRoute — real road-following polyline for the focused trip via
 * the backend's OSRM proxy. Falls back to the honest straight-line tether
 * (still dashed, so it visually reads as "approximate") if OSRM has no
 * route yet or the request failed — never blocks the map on a network call.
 */
function FocusedTripRoute({ trip, colorForStatus }) {
  const pickup = trip?.pickup;
  const dropoff = trip?.dropoff;
  const { coords } = useOsrmRoute(pickup, dropoff);
  const color = colorForStatus ? colorForStatus(trip.status) : "#4bb8f8";

  if (coords && coords.length > 1) {
    return (
      <Polyline
        positions={coords}
        pathOptions={{ color, weight: 3, opacity: 0.9 }}
      />
    );
  }
  return (
    <Polyline
      positions={[[pickup.lat, pickup.lng], [dropoff.lat, dropoff.lng]]}
      pathOptions={{ color, weight: 2.5, dashArray: "5 8", opacity: 0.6 }}
    />
  );
}

/**
 * WeatherLayer — OpenWeatherMap precipitation tiles overlaid on the map.
 *
 * If `REACT_APP_OPENWEATHER_API_KEY` is set, uses the full-quality precipitation
 * layer. Otherwise degrades gracefully to a no-op with a console note. Free
 * tier is 1000 calls/day — plenty for a dispatcher's session.
 */
function WeatherLayer() {
  const key = process.env.REACT_APP_OPENWEATHER_API_KEY;
  if (!key) return null; // Button label communicates the no-key state.
  return (
    <TileLayer
      url={`https://tile.openweathermap.org/map/precipitation_new/{z}/{x}/{y}.png?appid=${key}`}
      opacity={0.62}
      zIndex={410}
    />
  );
}

/**
 * ZonesOverlay — outlines + labels for each operating zone. A convex-hull
 * approximation over the zone's ZIP centroids, not a survey boundary —
 * admin-editable any time from the Zones & Regions admin screen.
 */
function ZonesOverlay() {
  const [zones, setZones] = useState([]);
  useEffect(() => {
    let alive = true;
    apiClient.get("/zones").then(({ data }) => alive && setZones(Array.isArray(data) ? data : []));
    return () => { alive = false; };
  }, []);
  return (
    <>
      {zones.map((z) => z.polygon && z.polygon.length >= 3 && (
        <Polygon key={z.id} positions={z.polygon} pathOptions={{ color: "#7fd8b7", weight: 1.5, fillOpacity: 0.04, dashArray: "4 6" }}>
          <Tooltip direction="center" permanent opacity={0.85} className="zone-label-tooltip">{z.name}</Tooltip>
        </Polygon>
      ))}
    </>
  );
}

/** Shared fetch-on-range-change for the 3 stats overlays below. */
function useZoneStats(endpoint, range) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    apiClient.get(`/zones/stats/${endpoint}?range=${range}`).then(({ data }) => alive && setData(data)).catch(() => {});
    return () => { alive = false; };
  }, [endpoint, range]);
  return data;
}

/** HeatmapOverlay — zone shaded by trip-pickup volume for the chosen range. */
function HeatmapOverlay({ range }) {
  const data = useZoneStats("heatmap", range);
  if (!data) return null;
  return (
    <>
      {data.zones.map((z) => z.polygon && z.polygon.length >= 3 && (
        <Polygon key={z.zone_id} positions={z.polygon}
          pathOptions={{ color: "#ff8a3d", weight: 1, fillColor: "#ff8a3d", fillOpacity: 0.08 + z.intensity * 0.42 }}>
          <Tooltip direction="center" opacity={0.9}>{z.zone_name} · {z.trip_count} trip{z.trip_count === 1 ? "" : "s"}</Tooltip>
        </Polygon>
      ))}
    </>
  );
}

const COVERAGE_TIER_COLOR = { short: "#ff5050", ok: "#7fd8b7", surplus: "#4da8ff" };

/** CoverageOverlay — zone shaded by supply (drivers+TPs) vs demand: red when
 * short, green when balanced, blue when oversupplied for this range. */
function CoverageOverlay({ range }) {
  const data = useZoneStats("coverage", range);
  if (!data) return null;
  return (
    <>
      {data.zones.map((z) => z.polygon && z.polygon.length >= 3 && (
        <Polygon key={z.zone_id} positions={z.polygon}
          pathOptions={{
            color: COVERAGE_TIER_COLOR[z.tier] || COVERAGE_TIER_COLOR.ok, weight: 1.5,
            fillColor: COVERAGE_TIER_COLOR[z.tier] || COVERAGE_TIER_COLOR.ok,
            fillOpacity: z.tier === "ok" ? 0.07 : 0.22,
          }}>
          <Tooltip direction="center" opacity={0.9}>
            {z.zone_name} · {z.supply_count} available / {z.trip_count} trips · {z.tier.toUpperCase()}
          </Tooltip>
        </Polygon>
      ))}
    </>
  );
}

/** DeadheadOverlay — each zone shaded by how often its pickups went to a
 * driver tagged for a *different* zone, plus the citywide top-mismatch
 * summary pinned over the service area. */
function DeadheadOverlay({ range }) {
  const data = useZoneStats("deadhead", range);
  if (!data) return null;
  return (
    <>
      {(data.zones || []).map((z) => z.polygon && z.polygon.length >= 3 && (
        <Polygon key={z.zone_id} positions={z.polygon}
          pathOptions={{ color: "#e85d9c", weight: 1, fillColor: "#e85d9c", fillOpacity: z.mismatch_rate ? 0.08 + z.mismatch_rate * 0.5 : 0.03 }}>
          <Tooltip direction="center" opacity={0.9}>
            {z.zone_name} · {z.mismatched_count}/{z.assigned_count || 0} out-of-zone ({Math.round((z.mismatch_rate || 0) * 100)}%)
          </Tooltip>
        </Polygon>
      ))}
      <CircleMarker center={[41.88, -87.68]} radius={0} pathOptions={{ opacity: 0 }}>
        <Tooltip direction="top" permanent opacity={0.95} className="deadhead-summary-tooltip">
          {data.mismatched}/{data.total_assigned} assigned trips went out-of-zone ({Math.round((data.mismatch_rate || 0) * 100)}%)
          {data.pairs.length > 0 && ` — top: ${data.pairs[0].pair} (${data.pairs[0].count})`}
        </Tooltip>
      </CircleMarker>
    </>
  );
}

/** OverlayLegend — color-scale key for whichever stats overlay is active,
 * shown inside the map-layers drawer so a VA can read intensity at a glance. */
function OverlayLegend({ heatmapOn, coverageOn, deadheadOn }) {
  if (!heatmapOn && !coverageOn && !deadheadOn) return null;
  return (
    <div className="ml-legend" data-testid="overlay-legend">
      {heatmapOn && (
        <div className="ml-legend-row">
          <span className="ml-legend-swatch" style={{ background: "linear-gradient(90deg, rgba(255,138,61,0.08), rgba(255,138,61,0.5))" }} />
          <span>heat map — trip pickup volume (low → high)</span>
        </div>
      )}
      {coverageOn && (
        <div className="ml-legend-row">
          <span className="ml-legend-dot" style={{ background: COVERAGE_TIER_COLOR.short }} /><span>short</span>
          <span className="ml-legend-dot" style={{ background: COVERAGE_TIER_COLOR.ok }} /><span>balanced</span>
          <span className="ml-legend-dot" style={{ background: COVERAGE_TIER_COLOR.surplus }} /><span>surplus</span>
        </div>
      )}
      {deadheadOn && (
        <div className="ml-legend-row">
          <span className="ml-legend-swatch" style={{ background: "linear-gradient(90deg, rgba(232,93,156,0.08), rgba(232,93,156,0.55))" }} />
          <span>deadhead mismatch — out-of-zone assignment rate (low → high)</span>
        </div>
      )}
    </div>
  );
}

/**
 * handleTripDrop — resolve a Leaflet dragEnd event into an assignment.
 *
 * Called when the VA drops a trip halo somewhere on the map. Finds the
 * closest live driver puck within THRESHOLD_METERS; if within range, fires
 * the canonical `POST /tpt/trips/{id}/assign` with that driver_id + their
 * tp_id (drivers are always TP-scoped). Otherwise: snap the halo back.
 *
 * Returns true if an assignment was attempted, false if snap-back needed.
 */
const _DROP_THRESHOLD_METERS = 500;   // 500m — generous, city-block scale

async function handleTripDrop(trip, dropLatLng, drivers, assignTrip) {
  if (!Array.isArray(drivers) || drivers.length === 0) return false;
  if (!assignTrip) return false;

  // Haversine — meters
  const R = 6371000;
  const rad = (d) => (d * Math.PI) / 180;
  let best = null;
  let bestDist = Infinity;
  for (const d of drivers) {
    if (d.last_seen_lat == null || d.last_seen_lng == null) continue;
    const dLat = rad(d.last_seen_lat - dropLatLng.lat);
    const dLng = rad(d.last_seen_lng - dropLatLng.lng);
    const a = Math.sin(dLat / 2) ** 2
            + Math.cos(rad(dropLatLng.lat)) * Math.cos(rad(d.last_seen_lat))
              * Math.sin(dLng / 2) ** 2;
    const dist = 2 * R * Math.asin(Math.sqrt(a));
    if (dist < bestDist) { bestDist = dist; best = d; }
  }
  if (!best || bestDist > _DROP_THRESHOLD_METERS) return false;

  await assignTrip(trip.trip_id, {
    tp_id: best.tp_id,
    driver_id: best.driver_id,
    dispatch_at: new Date().toISOString(),
  });
  return true;
}

/**
 * GoogleTrafficLayer — attaches Google's live traffic layer to Leaflet via
 * the googlemutant plugin. Loads the Google JS API on first mount using the
 * same public API key we already use for /tp/map. No new env work needed.
 */
function GoogleTrafficLayer() {
  const map = useMap();
  const layerRef = useRef(null);
  useEffect(() => {
    const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!key) return undefined;

    const attach = () => {
      try {
        // eslint-disable-next-line no-undef
        const layer = L.gridLayer.googleMutant({
          type: "roadmap",
          styles: [{ featureType: "all", elementType: "all", stylers: [{ visibility: "off" }] }],
        });
        layer.addGoogleLayer("TrafficLayer");
        layer.addTo(map);
        layerRef.current = layer;
      } catch (_e) { /* google not ready or plugin failed */ }
    };

    // Load the Google Maps SDK once
    if (window.google?.maps) {
      attach();
    } else {
      const existing = document.getElementById("gmaps-sdk");
      if (existing) {
        existing.addEventListener("load", attach, { once: true });
      } else {
        const s = document.createElement("script");
        s.id = "gmaps-sdk";
        s.async = true;
        s.defer = true;
        s.src = `https://maps.googleapis.com/maps/api/js?key=${key}&v=weekly`;
        s.onload = attach;
        document.head.appendChild(s);
      }
    }

    return () => {
      if (layerRef.current) {
        try { map.removeLayer(layerRef.current); } catch { /* noop */ }
        layerRef.current = null;
      }
    };
  }, [map]);
  return null;
}

// Small floating glow that hints where Nova is "looking"
// When something is focused, it drifts near the workspace edge.
// When idle, it wanders slowly across the peripheral zone.
function NovaHalo() {
  const { focus, tickN } = useRoom();
  const style = useMemo(() => {
    const drift = (tickN * 0.6) % 100;
    if (focus) {
      // Sit near the workspace edge (right side)
      return { right: `28vw`, top: `${28 + Math.sin(tickN * 0.05) * 4}%` };
    }
    // Idle drift
    return {
      left: `${20 + drift * 0.4}%`,
      top: `${40 + Math.sin(tickN * 0.04) * 15}%`,
    };
  }, [focus, tickN]);
  return <div className="nova-halo" style={style} />;
}
